import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart2,
  BookOpen,
  Building2,
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  ExternalLink,
  FileText,
  Folder,
  Hash,
  Home,
  Landmark,
  Loader2,
  RefreshCw,
  ShoppingCart,
  Smartphone,
  TrendingUp,
  Upload,
  User,
  Video,
  XCircle,
} from 'lucide-react';
import UploadDocumentModal from './UploadDocumentModal';
import {
  Alert,
  Badge,
  CustomerStatusBadge,
  EmptyState,
  PageHeader,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  useToast,
  ToastContainer,
} from '../ui';
import { customersService } from '../../services/customers.service';

/* ── helpers ────────────────────────────────────────────────────────────────── */
const avatarInitials = (first, last, email) => {
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (email) return email[0].toUpperCase();
  return '?';
};

const fmt = (val) => val || '—';

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const maskIban = (iban) => {
  if (!iban || iban.length < 8) return iban;
  return `${iban.slice(0, 4)}•••••••••${iban.slice(-4)}`;
};

/* ── InlineCopy ─────────────────────────────────────────────────────────────── */
const InlineCopy = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="ml-1 text-ink-400 hover:text-ink-700 transition-colors inline-flex items-center"
      title={copied ? 'Copied!' : 'Copy'}
    >
      {copied ? <Check className="h-3 w-3 text-success-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
};

/* ── InfoRow (key-value inside a card) ──────────────────────────────────────── */
const InfoRow = ({ label, value, mono = false, copyable = false }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-ink-100 last:border-0">
    <span className="text-xs text-ink-500 shrink-0 w-40">{label}</span>
    <div className="flex items-center min-w-0 flex-1 justify-end text-right">
      <span className={`text-sm text-ink-800 break-all ${mono ? 'font-mono' : ''}`}>
        {value ?? '—'}
      </span>
      {copyable && value && <InlineCopy value={value} />}
    </div>
  </div>
);

/* ── Tab bar ────────────────────────────────────────────────────────────────── */
const TABS = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'bank-accounts', label: 'Bank Accounts', icon: CreditCard },
];

const TabBar = ({ active, onChange }) => (
  <div className="flex border-b border-ink-200 mb-6">
    {TABS.map((t) => {
      const Icon = t.icon;
      const isActive = active === t.id;
      return (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px
            ${isActive
              ? 'border-accent-600 text-accent-700'
              : 'border-transparent text-ink-500 hover:text-ink-800 hover:border-ink-300'
            }`}
        >
          <Icon className="h-3.5 w-3.5" />
          {t.label}
        </button>
      );
    })}
  </div>
);

/* ── Document type formatting ───────────────────────────────────────────────── */
const DOC_TYPE_LABELS = {
  PASSPORT: 'Passport',
  NATIONAL_ID: 'National ID',
  BANK_STATEMENT: 'Bank Statement',
  PROOF_OF_ADDRESS: 'Proof of Address',
  LIVNESS_VERIFICATION_VIDEO: 'Liveness Video',
  TAX_ID: 'Tax ID',
  W8: 'W-8BEN',
  W9: 'W-9',
  OTHER: 'Other',
};

const DOC_TONES = {
  PASSPORT: 'purple',
  NATIONAL_ID: 'info',
  BANK_STATEMENT: 'success',
  PROOF_OF_ADDRESS: 'neutral',
  W8: 'warning',
  W9: 'warning',
  TAX_ID: 'warning',
  OTHER: 'neutral',
};

const DOC_TYPE_ICONS = {
  PASSPORT: BookOpen,
  NATIONAL_ID: CreditCard,
  BANK_STATEMENT: Landmark,
  PROOF_OF_ADDRESS: Home,
  LIVNESS_VERIFICATION_VIDEO: Video,
  TAX_ID: Hash,
  W8: FileText,
  W9: FileText,
  OTHER: Folder,
};

const ICON_TONE_CLASSES = {
  purple: 'bg-accent-50 text-accent-600',
  info: 'bg-info-50 text-info-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  neutral: 'bg-ink-100 text-ink-500',
};

const REQUIRED_DOCUMENT_TYPES = [
  { type: 'PASSPORT', label: 'Passport', altTypes: ['NATIONAL_ID'], altLabel: 'or National ID' },
  { type: 'PROOF_OF_ADDRESS', label: 'Proof of Address', altTypes: [], altLabel: null },
  { type: 'LIVNESS_VERIFICATION_VIDEO', label: 'Liveness Video', altTypes: [], altLabel: null },
];

const computeMissingDocs = (docs) => {
  const uploadedTypes = new Set((docs ?? []).map((d) => d.documentType));
  return REQUIRED_DOCUMENT_TYPES.filter(
    (req) => ![req.type, ...req.altTypes].some((t) => uploadedTypes.has(t)),
  );
};

/* ── Country code → name map (common codes) ─────────────────────────────────── */
const COUNTRY_NAMES = {
  AE: 'United Arab Emirates',
  US: 'United States',
  GB: 'United Kingdom',
  SA: 'Saudi Arabia',
  IN: 'India',
  EG: 'Egypt',
  PK: 'Pakistan',
  BD: 'Bangladesh',
  PH: 'Philippines',
  JO: 'Jordan',
  KW: 'Kuwait',
  BH: 'Bahrain',
  QA: 'Qatar',
  OM: 'Oman',
  LB: 'Lebanon',
  MA: 'Morocco',
  TN: 'Tunisia',
  NG: 'Nigeria',
  KE: 'Kenya',
  ZA: 'South Africa',
};

const countryLabel = (code) => {
  if (!code) return null;
  const name = COUNTRY_NAMES[code.toUpperCase()] ?? code;
  try {
    const flag = code.toUpperCase().replace(/./g, (c) =>
      String.fromCodePoint(c.charCodeAt(0) + 127397),
    );
    return `${flag} ${name}`;
  } catch {
    return name;
  }
};

/* ── Document file kind (derived from the uploaded file's extension) ───────── */
const FILE_KIND_LABELS = {
  pdf: 'PDF', doc: 'DOC', docx: 'DOCX', xls: 'XLS', xlsx: 'XLSX',
  jpg: 'JPG', jpeg: 'JPG', png: 'PNG', gif: 'GIF', webp: 'WEBP', svg: 'SVG',
  mp4: 'MP4', mov: 'MOV', webm: 'WEBM',
};
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm']);
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']);

const fileKindOf = (fileKey) => {
  const ext = fileKey?.split('.').pop()?.toLowerCase();
  if (!ext) return { label: '—', tone: 'neutral' };
  const label = FILE_KIND_LABELS[ext] || ext.toUpperCase();
  const tone = VIDEO_EXTENSIONS.has(ext) ? 'purple' : IMAGE_EXTENSIONS.has(ext) ? 'info' : 'neutral';
  return { label, tone };
};

/* ── Document expiry status (derived from expiryDate) ───────────────────────── */
const expiryStatus = (iso) => {
  if (!iso) return null;
  const daysLeft = Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return { label: 'Expired', tone: 'danger' };
  if (daysLeft <= 30) return { label: 'Expiring soon', tone: 'warning' };
  return null;
};

/* ── Relative "time ago" for recent uploads (falls back to the exact date) ──── */
const timeAgo = (iso) => {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return null;
};

/* ── OVERVIEW TAB ───────────────────────────────────────────────────────────── */
const OverviewTab = ({ customer, docs, docsLoading, showToast }) => {
  if (!customer) return null;
  const isFunded = customer.status === 'ACTIVE';
  const uploadedTypes = new Set((docs ?? []).map((d) => d.documentType));
  const hasAddress = customer.address && Object.values(customer.address).some(Boolean);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Personal Information */}
      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-100">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Personal Information</p>
        </div>
        <div className="px-5">
          <InfoRow label="Full name" value={`${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim() || '—'} />
          <InfoRow label="Email" value={customer.email} copyable />
          <InfoRow label="Phone" value={fmt(customer.phone)} mono />
          {customer.dateOfBirth && (
            <InfoRow label="Date of birth" value={fmtDate(customer.dateOfBirth)} />
          )}
          {customer.countryOfResidence && (
            <InfoRow label="Nationality" value={countryLabel(customer.countryOfResidence)} />
          )}
          {customer.localCurrency && (
            <InfoRow label="Local currency" value={customer.localCurrency} mono />
          )}
        </div>
      </div>

      {/* Account */}
      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-100">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Account</p>
        </div>
        <div className="px-5">
          <InfoRow label="Customer ID" value={customer.customerId} mono copyable />
          <InfoRow label="External ref" value={customer.externalReference} mono copyable />
          <InfoRow label="Reference code" value={customer.referenceCode} mono copyable />
          <InfoRow label="Created" value={fmtDate(customer.createdAt)} />
          {customer.updatedAt && (
            <InfoRow label="Last updated" value={fmtDate(customer.updatedAt)} />
          )}
          <div className="flex items-start justify-between gap-4 py-3">
            <span className="text-xs text-ink-500 shrink-0 w-40">Status</span>
            <div className="flex items-center justify-end flex-1">
              <CustomerStatusBadge status={customer.status} />
            </div>
          </div>
        </div>
      </div>

      {/* Address (conditional) */}
      {hasAddress && (
        <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-ink-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Address</p>
          </div>
          <div className="px-5">
            {customer.address.building && (
              <InfoRow label="Building" value={customer.address.building} />
            )}
            {customer.address.apartment && (
              <InfoRow label="Apartment" value={customer.address.apartment} />
            )}
            {customer.address.area && (
              <InfoRow label="Area" value={customer.address.area} />
            )}
            {customer.address.country && (
              <InfoRow label="Country" value={countryLabel(customer.address.country)} />
            )}
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-100">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Quick actions</p>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 gap-3">
          <Link
            to={`/portfolio/${customer.customerId}`}
            className="flex flex-col items-center gap-2 rounded-xl border border-ink-200 p-4 text-center hover:border-accent-300 hover:bg-accent-50 transition-colors group"
          >
            <TrendingUp className="h-5 w-5 text-ink-400 group-hover:text-accent-600 transition-colors" />
            <p className="text-xs font-medium text-ink-700">Portfolio</p>
          </Link>
          <Link
            to={`/orders?customerId=${customer.customerId}`}
            className="flex flex-col items-center gap-2 rounded-xl border border-ink-200 p-4 text-center hover:border-accent-300 hover:bg-accent-50 transition-colors group"
          >
            <ShoppingCart className="h-5 w-5 text-ink-400 group-hover:text-accent-600 transition-colors" />
            <p className="text-xs font-medium text-ink-700">Orders</p>
          </Link>
          <Link
            to={`/financials?customerId=${customer.customerId}`}
            className="flex flex-col items-center gap-2 rounded-xl border border-ink-200 p-4 text-center hover:border-accent-300 hover:bg-accent-50 transition-colors group"
          >
            <Building2 className="h-5 w-5 text-ink-400 group-hover:text-accent-600 transition-colors" />
            <p className="text-xs font-medium text-ink-700">Cash In/Out</p>
          </Link>
          {isFunded && (
            <SessionButton customerId={customer.customerId} showToast={showToast} />
          )}
        </div>
      </div>

      {/* Document checklist */}
      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-100">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Document checklist</p>
        </div>
        <div className="px-5 py-4">
          {docsLoading ? (
            <div className="flex items-center gap-2 text-ink-400 text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {REQUIRED_DOCUMENT_TYPES.map((req) => {
                const satisfied = [req.type, ...req.altTypes].some((t) => uploadedTypes.has(t));
                return (
                  <div key={req.type} className="flex items-center gap-1.5">
                    {satisfied
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
                      : <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    }
                    <span className={`text-xs ${satisfied ? 'text-ink-700' : 'text-red-600 font-medium'}`}>
                      {req.label}
                      {req.altLabel && <span className="text-ink-400 font-normal"> {req.altLabel}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


/* ── Session button ─────────────────────────────────────────────────────────── */
const SessionButton = ({ customerId, showToast }) => {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const result = await customersService.generateSession(customerId);
      const sessionKey = result?.sessionKey || result?.session_key;
      if (sessionKey) {
        setUrl(sessionKey);
      } else {
        showToast?.('Session generated but no key was returned. Please retry.', 'danger');
      }
    } catch (err) {
      showToast?.(err?.response?.data?.message || 'Failed to generate embed session. Please retry.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  if (url) {
    return (
      <button
        type="button"
        onClick={() => { navigator.clipboard.writeText(url); setUrl(null); }}
        className="flex flex-col items-center gap-2 rounded-xl border border-success-200 bg-success-50 p-4 text-center transition-colors"
        title="Click to copy session key"
      >
        <Check className="h-5 w-5 text-success-600" />
        <p className="text-xs font-medium text-success-700">Key copied!</p>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={generate}
      disabled={loading}
      className="flex flex-col items-center gap-2 rounded-xl border border-ink-200 p-4 text-center hover:border-accent-300 hover:bg-accent-50 transition-colors group disabled:opacity-50"
    >
      {loading
        ? <Loader2 className="h-5 w-5 text-ink-400 animate-spin" />
        : <Smartphone className="h-5 w-5 text-ink-400 group-hover:text-accent-600 transition-colors" />
      }
      <p className="text-xs font-medium text-ink-700">Session</p>
    </button>
  );
};

/* ── DOCUMENTS TAB ──────────────────────────────────────────────────────────── */
const DocumentsTab = ({ docs, docsLoading, docsError, onLoad, onUploadClick }) => {
  const missingDocs = docsLoading || docsError ? [] : computeMissingDocs(docs);

  if (docsLoading) return (
    <div className="flex items-center justify-center py-12 gap-2 text-ink-500">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span className="text-sm">Loading documents…</span>
    </div>
  );
  if (docsError) return (
    <Alert tone="danger" action={
      <button type="button" onClick={onLoad} className="btn btn-sm btn-secondary">Retry</button>
    }>{docsError}</Alert>
  );

  return (
    <div className="space-y-4">
      {missingDocs.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="missing-docs-banner">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800 mb-2">Missing required documents</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {missingDocs.map((req) => (
                <span
                  key={req.type}
                  className="bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 text-xs font-medium"
                >
                  {req.label}
                </span>
              ))}
            </div>
            <button
              type="button"
              onClick={onUploadClick}
              className="text-xs text-amber-700 hover:text-amber-900 font-medium underline underline-offset-2"
            >
              Upload missing documents →
            </button>
          </div>
        </div>
      )}

      {(!docs || docs.length === 0) ? (
        <EmptyState icon={FileText} title="No documents" description="No KYC documents have been submitted for this customer yet." />
      ) : (
        <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
              Uploaded documents ({docs.length})
            </p>
          </div>
          <Table>
            <THead>
              <TR>
                <TH className="w-56">Document</TH>
                <TH className="w-20">File</TH>
                <TH className="w-40">Validity</TH>
                <TH className="w-40">Country</TH>
                <TH className="w-32">Uploaded</TH>
                <TH>Key</TH>
              </TR>
            </THead>
            <TBody>
              {docs.map((doc, i) => {
                const expiry = expiryStatus(doc.expiryDate);
                const fileKind = fileKindOf(doc.fileKey);
                const TypeIcon = DOC_TYPE_ICONS[doc.documentType] || FileText;
                const iconTone = ICON_TONE_CLASSES[DOC_TONES[doc.documentType]] || ICON_TONE_CLASSES.neutral;
                const uploaded = timeAgo(doc.uploadedAt) || fmtDateTime(doc.uploadedAt);
                return (
                  <TR key={i}>
                    <TD>
                      <div className="flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${iconTone}`}>
                          <TypeIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink-900 truncate">
                            {DOC_TYPE_LABELS[doc.documentType] || doc.documentType}
                          </p>
                          {doc.documentNumber && (
                            <p className="text-xs text-ink-400 font-mono truncate">{doc.documentNumber}</p>
                          )}
                        </div>
                      </div>
                    </TD>
                    <TD>
                      <Badge tone={fileKind.tone} className="uppercase">
                        {fileKind.label}
                      </Badge>
                    </TD>
                    <TD>
                      {doc.issueDate || doc.expiryDate ? (
                        <div className="text-xs text-ink-600 space-y-1">
                          {doc.issueDate && <div>Issued {fmtDate(doc.issueDate)}</div>}
                          {doc.expiryDate && (
                            <div className="flex items-center gap-1.5">
                              <span>Expires {fmtDate(doc.expiryDate)}</span>
                              {expiry && <Badge tone={expiry.tone}>{expiry.label}</Badge>}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-ink-400 text-xs">—</span>
                      )}
                    </TD>
                    <TD>
                      <span className="text-xs text-ink-700">{countryLabel(doc.issuingCountry) || '—'}</span>
                    </TD>
                    <TD>
                      <span className="text-xs text-ink-600" title={fmtDateTime(doc.uploadedAt)}>
                        {uploaded}
                      </span>
                    </TD>
                    <TD>
                      {doc.fileKey ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs text-ink-500 truncate max-w-[180px]">{doc.fileKey}</span>
                          <InlineCopy value={doc.fileKey} />
                        </div>
                      ) : (
                        <span className="text-ink-400 text-xs">—</span>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </div>
      )}
    </div>
  );
};

/* ── BANK ACCOUNTS TAB ──────────────────────────────────────────────────────── */
const BankAccountsTab = ({ customerId }) => {
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    customersService
      .listBankAccounts(customerId)
      .then((data) => setAccounts(data?.items ?? (Array.isArray(data) ? data : [])))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load bank accounts.'))
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-12 gap-2 text-ink-500">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span className="text-sm">Loading bank accounts…</span>
    </div>
  );
  if (error) return (
    <Alert tone="danger" action={
      <button type="button" onClick={load} className="btn btn-sm btn-secondary">Retry</button>
    }>{error}</Alert>
  );
  if (!accounts || accounts.length === 0) return (
    <EmptyState icon={CreditCard} title="No bank accounts" description="No linked bank accounts found for this customer." />
  );

  return (
    <Table>
      <THead>
        <TR>
          <TH>Bank</TH>
          <TH>Beneficiary</TH>
          <TH>IBAN</TH>
          <TH className="w-24">SWIFT</TH>
          <TH className="w-20">Currency</TH>
          <TH className="w-20">Status</TH>
          <TH className="w-28">Added</TH>
        </TR>
      </THead>
      <TBody>
        {accounts.map((acc) => (
          <TR key={acc.bankAccountId}>
            <TD>
              <p className="text-sm font-medium text-ink-900">{fmt(acc.bankName)}</p>
            </TD>
            <TD>
              <span className="text-sm text-ink-700">{fmt(acc.beneficiaryName)}</span>
            </TD>
            <TD>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs text-ink-700">{maskIban(acc.iban)}</span>
                {acc.iban && <InlineCopy value={acc.iban} />}
              </div>
            </TD>
            <TD>
              <span className="font-mono text-xs text-ink-500">{fmt(acc.swiftCode)}</span>
            </TD>
            <TD>
              <span className="text-xs font-semibold text-ink-700">{fmt(acc.currency)}</span>
            </TD>
            <TD>
              <Badge tone={acc.isActive ? 'success' : 'neutral'} dot>
                {acc.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </TD>
            <TD>
              <span className="text-xs text-ink-500">{fmtDate(acc.createdAt)}</span>
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
};

/* ── main component ─────────────────────────────────────────────────────────── */
const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { toasts, show, dismiss } = useToast();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [docsKey, setDocsKey] = useState(0);

  const [docs, setDocs] = useState(null);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsError, setDocsError] = useState(null);

  const loadCustomer = useCallback(() => {
    setLoading(true);
    setError(null);
    customersService
      .getCustomer(customerId)
      .then((data) => setCustomer(data?.user ?? data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load customer.'))
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => { loadCustomer(); }, [loadCustomer]);

  const loadDocs = useCallback(() => {
    setDocsLoading(true);
    setDocsError(null);
    customersService
      .getDocuments(customerId)
      .then((data) => setDocs(Array.isArray(data) ? data : (data?.documents ?? [])))
      .catch((err) => setDocsError(err?.response?.data?.message || 'Failed to load documents.'))
      .finally(() => setDocsLoading(false));
  }, [customerId]);

  useEffect(() => { loadDocs(); }, [loadDocs, docsKey]);

  const handleCopyId = () => {
    navigator.clipboard.writeText(customerId).then(() => show('Customer ID copied', 'success'));
  };

  const initials = avatarInitials(customer?.firstName, customer?.lastName, customer?.email);
  const fullName = customer
    ? `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim() || customer.email
    : '…';

  return (
    <div>
      <ToastContainer toasts={toasts} onDismiss={dismiss} />

      <PageHeader
        title={loading ? 'Customer' : fullName}
        description={customer?.email}
        breadcrumb={['Customers', loading ? '…' : fullName]}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/customers')} className="btn btn-sm btn-secondary">
              <ArrowLeft className="h-3.5 w-3.5" /> All customers
            </button>
            <button type="button" onClick={handleCopyId} className="btn btn-sm btn-secondary">
              <Copy className="h-3.5 w-3.5" /> Copy ID
            </button>
            {activeTab === 'documents' && (
              <button type="button" onClick={() => setUploadOpen(true)} className="btn btn-sm btn-primary">
                <Upload className="h-3.5 w-3.5" /> Upload document
              </button>
            )}
            <button type="button" onClick={loadCustomer} className="btn btn-sm btn-secondary">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading customer…</span>
        </div>
      ) : error ? (
        <Alert tone="danger" action={
          <button type="button" onClick={loadCustomer} className="btn btn-sm btn-secondary">Retry</button>
        }>{error}</Alert>
      ) : customer ? (
        <>
          {/* Header card */}
          <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden mb-6">
            <div className="px-6 py-5 flex items-center gap-5 flex-wrap">
              {/* Avatar */}
              <div className="h-14 w-14 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-lg font-bold shrink-0">
                {initials}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h2 className="text-base font-semibold text-ink-900">
                    {customer.firstName} {customer.lastName}
                  </h2>
                  <CustomerStatusBadge status={customer.status} />
                </div>
                <div className="mt-1 flex items-center gap-4 flex-wrap">
                  <span className="text-sm text-ink-500">{customer.email}</span>
                  {customer.phone && (
                    <span className="text-sm text-ink-500 font-mono">{customer.phone}</span>
                  )}
                </div>
              </div>
              {/* Meta pills */}
              {customer.referenceCode && (
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <div className="flex items-center gap-1 rounded-lg bg-ink-50 border border-ink-200 px-3 py-1.5">
                    <span className="text-2xs text-ink-400 uppercase tracking-wider mr-1">REF</span>
                    <span className="text-xs font-mono text-ink-700">{customer.referenceCode}</span>
                    <InlineCopy value={customer.referenceCode} />
                  </div>
                </div>
              )}
            </div>

            {/* Quick-stat bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 border-t border-ink-100 divide-x divide-ink-100">
              {[
                { label: 'Customer ID', value: customer.customerId, mono: true, copyable: true },
                { label: 'User ID', value: customer.userId, mono: true, copyable: true },
                { label: 'External ref', value: customer.externalReference || '—', mono: true },
                { label: 'Joined', value: fmtDate(customer.createdAt) },
              ].map((item) => (
                <div key={item.label} className="px-5 py-3">
                  <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400 mb-1">{item.label}</p>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs text-ink-700 truncate ${item.mono ? 'font-mono' : ''}`}>
                      {item.value}
                    </span>
                    {item.copyable && item.value && item.value !== '—' && <InlineCopy value={item.value} />}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <TabBar active={activeTab} onChange={setActiveTab} />

          {activeTab === 'overview' && (
            <OverviewTab customer={customer} docs={docs} docsLoading={docsLoading} showToast={show} />
          )}
          {activeTab === 'documents' && (
            <DocumentsTab
              docs={docs}
              docsLoading={docsLoading}
              docsError={docsError}
              onLoad={loadDocs}
              onUploadClick={() => setUploadOpen(true)}
            />
          )}
          {activeTab === 'bank-accounts' && <BankAccountsTab customerId={customerId} />}
        </>
      ) : null}

      {uploadOpen && (
        <UploadDocumentModal
          customerId={customerId}
          onClose={() => setUploadOpen(false)}
          onSuccess={() => { setDocsKey((k) => k + 1); show('Document uploaded successfully', 'success'); }}
        />
      )}
    </div>
  );
};

export default CustomerDetailPage;
